'use client'

import { OLIVE_PLAN_CARDS, type OlivePlanCard as PlanCard, type PricingPlanId } from './olivePlanData'

export type { PricingPlanId }
import { userFacingErrorMessage } from '@/lib/apiErrors'
import { TeamCheckoutDiscount } from '@/components/billing/TeamCheckoutDiscount'
import { parsePlanId, type TeamCheckoutQuote } from '@/types/shared'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import * as Sentry from '@sentry/nextjs'
import { AlertCircle, ArrowRight, Check, Loader2 } from 'lucide-react'
import { useExtracted, useFormatter } from 'next-intl'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useAuth } from '@/hooks/useAuth'
import { useBillingPricingInfo } from '@/hooks/useBillingPricingInfo'
import { useStoredSignupAccess } from '@/hooks/useStoredSignupAccess'
import { clearStoredPromoCode, getStoredPromoCode, storePromoCode } from '@/lib/signupAccessStorage'
import { useStoredPromoCampaign } from '@/hooks/useStoredPromoCampaign'
import { usePromoCodeHandoff } from '@/hooks/usePromoCodeHandoff'
import { createTeamCheckout, previewPlanCheckout, subscribeToPlan } from '@/lib/api'
import type { BillingPricingInfo } from '@/lib/api'
import { trackFirstMonthPromoShown, trackPlanCheckoutStarted, trackTeamCheckoutStarted } from '@/lib/analytics'
import { PLAN_MONTHLY_OLIVES, PLAN_PRO, PLAN_STUDIO } from '@/lib/constants'
import { useOliveOutcomeLabel } from '@/lib/useOliveOutcomeLabel'
// Imported directly (not via the @/components/auth barrel) so PlanCards.test.ts
// keeps a module graph vitest can resolve.
import { SignUpModal } from '@/components/auth/SignUpModal'

interface AuthenticatedCta {
  label: string
  disabled?: boolean
  loading?: boolean
  onClick: () => void
}

const INCLUDES: Record<string, string> = {
  pro: 'Everything in Indie, plus',
  studio: 'Everything in Pro, plus',
  enterprise: 'Everything in Studio, plus',
}

const PLANS = OLIVE_PLAN_CARDS

const PLAN_RANK: Record<PlanCard['planId'], number> = {
  default: 0,
  pro: 1,
  studio: 2,
  enterprise: 3,
}

const CHECKOUT_NAVIGATION_FALLBACK_MS = 8000

/**
 * The pricing card a plan id sits on, by tier: comp and fal-key variants share
 * their tier's card, so a fal-key Pro customer sees Pro as their current plan
 * instead of an "Upgrade to Pro" that would buy the olive plan on top. Workshop
 * and unknown ids read as Indie.
 */
export function normalizePricingPlanId(planId: string | null | undefined): PricingPlanId {
  const { tier } = parsePlanId(planId)
  if (tier === 'pro' || tier === 'studio' || tier === 'enterprise') return tier
  return 'default'
}

/**
 * The 50%-off-first-month strike-through prices (Pricing v2 Stage 1). Fixed
 * display copy — the actual discount is a Stripe coupon the backend applies;
 * the caller passes per-plan eligibility (Pro: this user's first-time flag;
 * Studio: promo live + team creation actionable, since a new team always
 * qualifies).
 */
export function getFirstMonthPromo(
  planId: PricingPlanId,
  eligible: boolean | undefined,
): { was: string; now: string } | null {
  if (eligible !== true) return null
  if (planId === 'pro') return { was: '$30', now: '$15' }
  if (planId === 'studio') return { was: '$150', now: '$75' }
  return null
}

/**
 * Price copy for a card once a promo code is applied. The card must show what
 * the code does, not the automatic 50% strike-through it replaced — otherwise a
 * 100%-off code still reads as "$15". Pure so it can be unit-tested.
 */
export function getCodePromo(
  quote: Pick<TeamCheckoutQuote, 'promotionCode' | 'amount' | 'regularAmount' | 'discountMonths'> | null,
  money: (amount: number) => string,
): { was: string; now: string; percent: number; months: number | null; code: string } | null {
  if (!quote?.promotionCode || quote.regularAmount <= 0) return null
  return {
    was: money(quote.regularAmount),
    now: money(quote.amount),
    percent: Math.max(0, Math.round((1 - quote.amount / quote.regularAmount) * 100)),
    months: quote.discountMonths,
    code: quote.promotionCode,
  }
}

export function getAuthenticatedPricingCtaState(
  planId: PricingPlanId,
  effectivePlan: PricingPlanId,
  options: { hasTeamOrg?: boolean } = {},
): Pick<AuthenticatedCta, 'label' | 'disabled'> | null {
  const currentRank = PLAN_RANK[effectivePlan]
  const planRank = PLAN_RANK[planId]

  if (planId === 'enterprise') return null

  if (planId === 'default') {
    return {
      label: effectivePlan === 'default' ? 'Current plan' : 'Go to Projects',
      disabled: effectivePlan === 'default',
    }
  }

  if (currentRank >= planRank) {
    return {
      label: effectivePlan === planId ? 'Current plan' : 'Included',
      disabled: true,
    }
  }

  if (planId === 'studio' && options.hasTeamOrg) {
    return {
      label: 'Included',
      disabled: true,
    }
  }

  if (planId === 'pro') {
    return { label: 'Upgrade to Pro' }
  }

  return { label: 'Create Studio team' }
}

export function isPricingBillingUnavailable(
  isRealAccount: boolean,
  billingInfo: Pick<BillingPricingInfo, 'billingEnabled'> | null,
  billingError: Error | null,
): boolean {
  return isRealAccount && ((!billingInfo && !!billingError) || billingInfo?.billingEnabled === false)
}

export function PlanCards() {
  const router = useRouter()
  const format = useFormatter()
  // `/pricing?code=X` (thank-you emails, partner links) pre-applies X on the Pro
  // card. Stored so a logged-out click survives signup: the coupon field only
  // mounts for signed-in accounts, and signup lands on /select-plan.
  const urlCode = useSearchParams().get('code')?.trim() || undefined
  const [handedOffCode] = useState(() => urlCode ?? getStoredPromoCode() ?? undefined)
  useEffect(() => {
    if (urlCode) storePromoCode(urlCode)
  }, [urlCode])
  const t = useExtracted()
  const outcomeLabel = useOliveOutcomeLabel()
  const { auth, isLoading: authLoading, isAnonymous } = useAuth()
  const { billingPricingInfo, isLoading: billingLoading, error: billingError } = useBillingPricingInfo()
  const hasSignupAccess = useStoredSignupAccess()
  const { campaign: promoCampaign } = useStoredPromoCampaign()
  const [showSignUpModal, setShowSignUpModal] = useState(false)
  const [subscribingPlan, setSubscribingPlan] = useState<string | null>(null)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  const [showCreateTeamDialog, setShowCreateTeamDialog] = useState(false)
  const [newTeamName, setNewTeamName] = useState('')
  const [teamCheckoutQuote, setTeamCheckoutQuote] = useState<TeamCheckoutQuote | null>(null)
  const [canCheckoutTeam, setCanCheckoutTeam] = useState(true)
  const [proCheckoutQuote, setProCheckoutQuote] = useState<TeamCheckoutQuote | null>(null)
  const [canCheckoutPro, setCanCheckoutPro] = useState(true)
  const [teamCreateError, setTeamCreateError] = useState<string | null>(null)
  const checkoutInFlightRef = useRef(false)
  const checkoutUnlockTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isRealAccount = !!auth?.userId && !isAnonymous
  const effectivePlan = normalizePricingPlanId(
    billingPricingInfo?.effectivePlan ??
      (billingPricingInfo?.personalOrg?.subscriptionStatus === 'pending_checkout'
        ? 'default'
        : billingPricingInfo?.personalOrg?.subscriptionPlan),
  )
  const isLoadingAccountState =
    authLoading || (isRealAccount && (billingLoading || (!billingPricingInfo && !billingError)))
  const isBillingUnavailable = isPricingBillingUnavailable(isRealAccount, billingPricingInfo, billingError)
  const isCheckoutInFlight = subscribingPlan !== null
  const isProUser = effectivePlan === 'pro'
  const hasTeamOrg = billingPricingInfo?.hasTeamOrg ?? false

  // A handed-off code may be a Pro or a team (Studio) code; resolve it once and
  // hand the quote to the matching card. The pickers wait until that is known.
  const handoff = usePromoCodeHandoff(
    handedOffCode,
    isRealAccount && !isLoadingAccountState && !isBillingUnavailable,
    result => {
      // The stored copy is consumed once there is a verdict: it only exists to
      // survive signup, and it must never override a later choice on this page.
      // An outage is not a verdict, so the code stays for the next visit.
      if (result.target !== 'unavailable') clearStoredPromoCode()
      if (result.target === 'pro') setProCheckoutQuote(result.quote)
      if (result.target === 'studio') setTeamCheckoutQuote(result.quote)
    },
  )
  // Checkout waits for the verdict so a fast click cannot skip the advertised discount.
  const handoffPending = Boolean(handedOffCode) && handoff === undefined
  // Only a code without a quote reaches the Pro picker as `initialCode`: it
  // re-runs the preview and shows the rejection (or the outage, with retry)
  // beside the code.
  const rejectedCode = handoff?.target === 'invalid' || handoff?.target === 'unavailable' ? handoff.code : undefined

  // 50%-off-first-month eligibility per plan. Pro follows this user's
  // first-time flag; Studio shows whenever the promo is live and creating a
  // team is actionable (a new team org always qualifies for the coupon).
  const showPromo = isRealAccount && !isLoadingAccountState && !isBillingUnavailable
  // Logged-out visitors from a promo campaign are definitionally new users, so
  // the strike price is true for them too. Gated on the REAL flag riding the
  // public campaign response — display can never outlive the coupon.
  const campaignPromoActive =
    !isRealAccount && !authLoading && promoCampaign?.active === true && promoCampaign.firstMonthPromoActive === true
  const proPromoEligible = (showPromo && billingPricingInfo?.firstMonthPromoEligible === true) || campaignPromoActive
  const studioPromoEligible =
    (showPromo &&
      billingPricingInfo?.firstMonthPromoActive === true &&
      !getAuthenticatedPricingCtaState('studio', effectivePlan, { hasTeamOrg })?.disabled) ||
    campaignPromoActive

  const promoShownRef = useRef(false)
  useEffect(() => {
    if (promoShownRef.current || (!proPromoEligible && !studioPromoEligible)) return
    promoShownRef.current = true
    if (proPromoEligible) trackFirstMonthPromoShown({ planId: 'pro', source: 'pricing_page' })
    if (studioPromoEligible) trackFirstMonthPromoShown({ planId: 'studio', source: 'pricing_page' })
  }, [proPromoEligible, studioPromoEligible])

  useEffect(() => {
    return () => {
      if (checkoutUnlockTimeoutRef.current) clearTimeout(checkoutUnlockTimeoutRef.current)
    }
  }, [])

  function clearCheckoutLock() {
    if (checkoutUnlockTimeoutRef.current) {
      clearTimeout(checkoutUnlockTimeoutRef.current)
      checkoutUnlockTimeoutRef.current = null
    }
    checkoutInFlightRef.current = false
    setSubscribingPlan(null)
  }

  function scheduleCheckoutUnlockFallback() {
    if (checkoutUnlockTimeoutRef.current) clearTimeout(checkoutUnlockTimeoutRef.current)
    checkoutUnlockTimeoutRef.current = setTimeout(() => {
      checkoutUnlockTimeoutRef.current = null
      checkoutInFlightRef.current = false
      setSubscribingPlan(null)
    }, CHECKOUT_NAVIGATION_FALLBACK_MS)
  }

  async function handleProCheckout(promotionCode?: string) {
    if (checkoutInFlightRef.current) return
    checkoutInFlightRef.current = true
    let shouldResetLock = true

    try {
      setSubscribingPlan(PLAN_PRO)
      setCheckoutError(null)
      setTeamCreateError(null)
      trackPlanCheckoutStarted({
        planId: PLAN_PRO,
        orgType: 'personal',
        source: 'pricing_page',
        firstMonthDiscount: promotionCode ? false : proPromoEligible,
      })
      const result = await subscribeToPlan(PLAN_PRO, undefined, { promotionCode })
      if (result.checkoutUrl) {
        shouldResetLock = false
        scheduleCheckoutUnlockFallback()
        window.location.assign(result.checkoutUrl)
        return
      }
      router.push('/settings/billing')
      shouldResetLock = false
      scheduleCheckoutUnlockFallback()
    } catch (err) {
      console.error('Failed to start Pro checkout:', err)
      Sentry.captureException(err, { tags: { action: 'pricing-proCheckout' } })
      setCheckoutError(userFacingErrorMessage(err, t('Failed to start checkout')))
    } finally {
      if (shouldResetLock) clearCheckoutLock()
    }
  }

  async function handleCreateTeam() {
    if (!newTeamName.trim() || checkoutInFlightRef.current || !canCheckoutTeam || handoffPending) return
    checkoutInFlightRef.current = true
    let shouldResetLock = true

    try {
      setSubscribingPlan(PLAN_STUDIO)
      setTeamCreateError(null)
      setCheckoutError(null)
      trackTeamCheckoutStarted({ teamName: newTeamName.trim(), planId: PLAN_STUDIO, source: 'pricing_page' })
      const result = await createTeamCheckout(newTeamName.trim(), PLAN_STUDIO, {
        promotionCode: teamCheckoutQuote?.promotionCode,
      })

      if (result.checkoutUrl) {
        shouldResetLock = false
        scheduleCheckoutUnlockFallback()
        window.location.assign(result.checkoutUrl)
        return
      }

      setShowCreateTeamDialog(false)
      setNewTeamName('')
      setTeamCheckoutQuote(null)
      setCanCheckoutTeam(false)
      router.push(`/team/activate?teamId=${result.teamId}`)
      shouldResetLock = false
      scheduleCheckoutUnlockFallback()
    } catch (err) {
      console.error('Failed to create team:', err)
      Sentry.captureException(err, { tags: { action: 'pricing-createTeamCheckout' } })
      setTeamCreateError(userFacingErrorMessage(err, t('Failed to create team. Please try again.')))
    } finally {
      if (shouldResetLock) clearCheckoutLock()
    }
  }

  function getAuthenticatedCta(plan: PlanCard): AuthenticatedCta | null {
    const state = getAuthenticatedPricingCtaState(plan.planId, effectivePlan, { hasTeamOrg })
    if (!state) return null

    if (plan.planId === 'pro') {
      if (state.disabled) {
        return { ...state, onClick: () => {} }
      }

      return {
        ...state,
        disabled: state.disabled || !canCheckoutPro || handoffPending,
        loading: subscribingPlan === PLAN_PRO,
        onClick: () => {
          setCheckoutError(null)
          void handleProCheckout(proCheckoutQuote?.promotionCode)
        },
      }
    }

    if (plan.planId === 'studio') {
      if (state.disabled) {
        return { ...state, onClick: () => {} }
      }

      return {
        ...state,
        disabled: state.disabled || !canCheckoutTeam || handoffPending,
        loading: subscribingPlan === PLAN_STUDIO,
        onClick: () => {
          setCheckoutError(null)
          setShowCreateTeamDialog(true)
        },
      }
    }

    return {
      ...state,
      onClick: () => router.push('/projects'),
    }
  }

  function getBillingUnavailableCta(plan: PlanCard): AuthenticatedCta | null {
    if (plan.planId !== 'default') return null
    return {
      label: 'Go to Projects',
      onClick: () => router.push('/projects'),
    }
  }

  const translateTagline = (tagline: string) => {
    switch (tagline) {
      case 'For passion projects':
        return t('For passion projects')
      case 'For solo professionals':
        return t('For solo professionals')
      case 'For teams':
        return t('For teams')
      case 'For bespoke solutions':
        return t('For bespoke solutions')
      default:
        return tagline
    }
  }

  const translateFeatureLabel = (label: string) => {
    switch (label) {
      case 'Public projects':
        return t('Public projects')
      case 'Private projects':
        return t('Private projects')
      case 'Billing users':
        return t('Billing users')
      case 'Support SLAs':
        return t('Support SLAs')
      case 'Custom integrations':
        return t('Custom integrations')
      default:
        return label
    }
  }

  const translateTooltip = (tooltip: string) => {
    switch (tooltip) {
      case 'Early access to experimental features':
        return t('Early access to experimental features')
      case 'Billing-only access for finance team':
        return t('Billing-only access for finance team')
      default:
        return tooltip
    }
  }

  const translatePrice = (price: string) => {
    switch (price) {
      case 'Free':
        return t('Free')
      case 'Custom':
        return t('Custom')
      default:
        return price
    }
  }

  const translateCtaLabel = (label: string, planId?: string) => {
    switch (label) {
      case 'Current plan':
        return t('Current plan')
      case 'Go to Projects':
        return t('Go to Projects')
      case 'Included':
        return t('Included')
      case 'Upgrade to Pro':
        return t('Upgrade to Pro')
      case 'Create Studio team':
        return t('Create Studio team')
      case 'Billing unavailable':
        return t('Billing unavailable')
      case 'Loading...':
        return t('Loading...')
      case 'Contact Us':
        return 'Contact sales'
      // Display only — `plan.cta === 'Request Access'` still routes signup vs waitlist.
      // Plain literal on purpose (feature-PR i18n policy); matches the homepage CTA.
      // Each paid card names its plan; the free one says Get started, like the rest of the site.
      case 'Request Access':
        return planId === 'pro' ? 'Get Pro' : planId === 'studio' ? 'Get Studio' : 'Get started'
      case 'Get started':
        return t('Get started')
      default:
        return label
    }
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div>
        {/* The taglines already say who each plan is for, so no group headers over the row. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {PLANS.map(plan => {
            const codeQuote =
              plan.planId === 'pro' ? proCheckoutQuote : plan.planId === 'studio' ? teamCheckoutQuote : null
            const codePromo = getCodePromo(codeQuote, amount =>
              format.number(amount, {
                style: 'currency',
                currency: codeQuote?.currency ?? 'usd',
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              }),
            )
            // An applied code replaces the automatic offer, so its copy replaces the strike-through too.
            const promo =
              codePromo ??
              getFirstMonthPromo(
                plan.planId,
                plan.planId === 'pro' ? proPromoEligible : plan.planId === 'studio' ? studioPromoEligible : false,
              )
            // Invite olive bonus (logged-out invite visitors only — signed-in users
            // check per-user redemption via /promo/me, not the stored slug).
            const inviteBonus =
              !authLoading && !isRealAccount ? (promoCampaign?.rewards[plan.planId]?.bonus_olives ?? 0) : 0
            const oliveBase = PLAN_MONTHLY_OLIVES[plan.planId] ?? 0
            const oliveWas = (chunks: ReactNode) => <span className="text-white/30 line-through">{chunks}</span>
            const oliveNow = (chunks: ReactNode) => <span className="font-medium text-white/90">{chunks}</span>

            return (
              <div key={plan.name} className="flex flex-col">
                {/* One card anatomy, shared with ByokPlans: name, price, who it's for,
                    hairline, features top-aligned, CTA pinned to the bottom. */}
                <div
                  className={`relative flex flex-1 flex-col rounded-2xl border p-6 md:p-7 ${
                    plan.highlighted
                      ? 'border-white/30 bg-white/[0.06] shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_24px_60px_-20px_rgba(255,255,255,0.12)]'
                      : 'border-white/10 bg-white/[0.02]'
                  }`}
                >
                  {plan.badge && (
                    <span className="absolute -top-2.5 start-6 rounded-full bg-white px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-black">
                      {t('Recommended')}
                    </span>
                  )}

                  <div>
                    <h3 className="text-[15px] font-medium text-white/70">{plan.name}</h3>
                    {/* The discounted price IS the price (Weavy pattern) — the
                        strike rides beside it, never in a footnote. */}
                    <p className="mt-3 text-[40px] font-semibold leading-none tracking-[-0.03em] text-white">
                      {promo ? (
                        <>
                          <span className="me-2 text-xl font-medium text-white/30 line-through">{promo.was}</span>
                          {promo.now}
                          <span className="ms-1 text-base font-normal tracking-normal text-white/40">{t('/mo')}</span>
                        </>
                      ) : (
                        <>
                          {translatePrice(plan.price)}
                          {plan.priceSuffix && (
                            <span className="ms-1 text-base font-normal tracking-normal text-white/40">{t('/mo')}</span>
                          )}
                        </>
                      )}
                    </p>
                    {/* Tagline sits directly under the price (matching the
                        non-promo layout) so it's never sandwiched between promo
                        lines. */}
                    <p className="mt-3 text-sm leading-snug text-white/50">{translateTagline(plan.tagline)}</p>
                    {/* One perk line carries both the discount and the invite
                        olive bonus, with "first month" said exactly once — instead
                        of two stacked near-identical lines. */}
                    {codePromo ? (
                      // Plain literals on purpose: feature PRs leave new copy for the scheduled i18n sweep.
                      <p className="mt-1.5 text-sm font-medium text-white/70">
                        {codePromo.months === null
                          ? `${codePromo.percent}% off with ${codePromo.code}`
                          : codePromo.months > 1
                            ? `${codePromo.percent}% off for ${codePromo.months} months with ${codePromo.code}`
                            : `${codePromo.percent}% off the first month with ${codePromo.code}`}
                      </p>
                    ) : promo && inviteBonus > 0 ? (
                      <p className="mt-1.5 text-sm font-medium text-white/70">
                        {t.rich('50% off + <was>{base}</was> <now>{total} olives</now> · first month', {
                          base: oliveBase.toLocaleString(),
                          total: (oliveBase + inviteBonus).toLocaleString(),
                          was: oliveWas,
                          now: oliveNow,
                        })}
                      </p>
                    ) : promo ? (
                      <p className="mt-1.5 text-sm font-medium text-white/70">{t('50% off the first month')}</p>
                    ) : inviteBonus > 0 ? (
                      <p className="mt-1.5 text-sm leading-snug text-white/50">
                        {t.rich('<was>{base}</was> <now>{total} olives</now> first month with your invite', {
                          base: oliveBase.toLocaleString(),
                          total: (oliveBase + inviteBonus).toLocaleString(),
                          was: oliveWas,
                          now: oliveNow,
                        })}
                      </p>
                    ) : null}
                  </div>

                  {/* Each paid card names what it builds on, so the four lists read as a ladder. */}
                  <div className="mt-6 border-t border-white/[0.08] pb-8 pt-6">
                    <p className="mb-4 h-4 text-xs text-white/40">{INCLUDES[plan.planId] ?? ''}</p>
                    <ul className="space-y-3">
                      {plan.features.map(feature => {
                        const tooltipText = feature.monthlyOlives
                          ? t('{count} olives {outcome}. Roll over for 12 months.', {
                              count: feature.monthlyOlives.toLocaleString(),
                              outcome: outcomeLabel(feature.monthlyOlives),
                            })
                          : feature.tooltip
                            ? translateTooltip(feature.tooltip)
                            : null
                        const label = translateFeatureLabel(feature.label)
                        return (
                          <li
                            key={feature.label}
                            className="flex items-start gap-2.5 text-sm leading-snug text-white/65"
                          >
                            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-white/35" strokeWidth={2} />
                            {/* The label itself is the tooltip trigger (dotted underline) —
                                no "?" icons competing with the text. */}
                            {tooltipText ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="cursor-help text-start underline decoration-dotted decoration-white/20 underline-offset-[5px] transition-colors hover:text-white"
                                  >
                                    {label}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[220px] text-xs">
                                  {tooltipText}
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <span>{label}</span>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  </div>

                  <PlanCta
                    plan={plan}
                    authenticatedCta={
                      isRealAccount && !isLoadingAccountState
                        ? isBillingUnavailable
                          ? getBillingUnavailableCta(plan)
                          : getAuthenticatedCta(plan)
                        : null
                    }
                    anonymousSignupCta={
                      plan.cta === 'Request Access' && !isRealAccount && hasSignupAccess
                        ? () => setShowSignUpModal(true)
                        : null
                    }
                    formatLabel={label => translateCtaLabel(label, plan.planId)}
                    isBillingUnavailable={isBillingUnavailable && (plan.planId === 'pro' || plan.planId === 'studio')}
                    isCheckoutInFlight={isCheckoutInFlight}
                    isLoadingAccountState={isLoadingAccountState}
                  />
                  {/* Promo-code slot. Every child is conditional, so the slot collapses
                      (empty:hidden) instead of reserving a blank row on every card. */}
                  <div className="mt-3 flex items-center text-white/70 empty:hidden">
                    {/* Logged-out `/pricing?code=X` visitor: the coupon field only mounts once
                        signed in, so say the code is kept rather than show nothing. The code is
                        unverified until then (the preview needs an account), hence "check".
                        Plain literal on purpose (feature-PR i18n policy). */}
                    {plan.planId === 'pro' && !isRealAccount && !authLoading && handedOffCode && (
                      <p className="text-xs text-white/50">Code {handedOffCode} saved. Log in to check and apply it.</p>
                    )}
                    {plan.planId === 'pro' &&
                      isRealAccount &&
                      !isLoadingAccountState &&
                      !isBillingUnavailable &&
                      !handoffPending &&
                      !getAuthenticatedPricingCtaState('pro', effectivePlan, { hasTeamOrg })?.disabled && (
                        <TeamCheckoutDiscount
                          planId={PLAN_PRO}
                          quote={proCheckoutQuote}
                          onChange={setProCheckoutQuote}
                          onReadyChange={setCanCheckoutPro}
                          preview={previewPlanCheckout}
                          previewOperation="plan_checkout_preview"
                          // First-time subscribers see the automatic offer as an applied row with
                          // Change (same as the team dialog); everyone else gets the coupon button.
                          optional={!proPromoEligible}
                          initialCode={rejectedCode}
                          hideTotal
                          disabled={isCheckoutInFlight}
                        />
                      )}
                    {plan.planId === 'studio' &&
                      isRealAccount &&
                      !isLoadingAccountState &&
                      !isBillingUnavailable &&
                      !showCreateTeamDialog &&
                      !handoffPending &&
                      !getAuthenticatedPricingCtaState('studio', effectivePlan, { hasTeamOrg })?.disabled && (
                        <TeamCheckoutDiscount
                          planId={PLAN_STUDIO}
                          quote={teamCheckoutQuote}
                          onChange={setTeamCheckoutQuote}
                          onReadyChange={setCanCheckoutTeam}
                          optional
                          hideTotal
                          disabled={isCheckoutInFlight}
                        />
                      )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        {checkoutError && (
          <div className="mt-4 flex items-start justify-center gap-2 text-sm text-red-300">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{checkoutError}</span>
          </div>
        )}
      </div>
      <Dialog
        open={showCreateTeamDialog}
        onOpenChange={open => {
          if (!open && checkoutInFlightRef.current) return
          setShowCreateTeamDialog(open)
          if (!open) {
            setTeamCreateError(null)
            setNewTeamName('')
            setTeamCheckoutQuote(quote => (quote?.promotionCode ? quote : null))
          }
        }}
      >
        <DialogContent
          showCloseButton={subscribingPlan !== PLAN_STUDIO}
          onEscapeKeyDown={e => {
            if (checkoutInFlightRef.current) e.preventDefault()
          }}
          onInteractOutside={e => {
            if (checkoutInFlightRef.current) e.preventDefault()
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('Create a Team')}</DialogTitle>
            <DialogDescription>
              {t('Set up your studio workspace with shared billing and access controls.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label htmlFor="pricing-team-name" className="block text-sm font-medium text-foreground">
              {t('Team name')}
            </label>
            <Input
              id="pricing-team-name"
              type="text"
              value={newTeamName}
              onChange={e => {
                setNewTeamName(e.target.value)
                if (teamCreateError) setTeamCreateError(null)
              }}
              placeholder={t('e.g. Martini Productions')}
              autoFocus
              disabled={subscribingPlan === PLAN_STUDIO}
              onKeyDown={e => {
                if (e.key === 'Enter' && newTeamName.trim() && !checkoutInFlightRef.current) {
                  void handleCreateTeam()
                }
              }}
              className="w-full"
            />
            <TeamCheckoutDiscount
              planId={PLAN_STUDIO}
              quote={teamCheckoutQuote}
              onReadyChange={setCanCheckoutTeam}
              onChange={setTeamCheckoutQuote}
              disabled={isCheckoutInFlight}
            />
            {teamCreateError && (
              <div className="flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{teamCreateError}</span>
              </div>
            )}
            {isProUser && (
              <p className="text-[11px] text-muted-foreground/60">
                {t("Your Pro plan will be canceled and you'll keep your personal olives.")}
              </p>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                if (!checkoutInFlightRef.current) setShowCreateTeamDialog(false)
              }}
              disabled={isCheckoutInFlight}
            >
              {t('Cancel')}
            </Button>
            <Button
              onClick={() => void handleCreateTeam()}
              disabled={!newTeamName.trim() || isCheckoutInFlight || !canCheckoutTeam}
            >
              {subscribingPlan === PLAN_STUDIO ? (
                <>
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                  {t('Creating...')}
                </>
              ) : (
                t('Continue')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* returnPath covers the Google OAuth flow; email flows complete
          in place, so onSuccess carries them to plan selection too. */}
      <SignUpModal
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        onSuccess={() => router.push('/select-plan')}
        returnPath="/select-plan"
        defaultMode="signup"
        variant="marketing"
      />
    </TooltipProvider>
  )
}

function PlanCta({
  plan,
  authenticatedCta,
  anonymousSignupCta,
  formatLabel,
  isBillingUnavailable,
  isCheckoutInFlight,
  isLoadingAccountState,
}: {
  plan: PlanCard
  authenticatedCta: AuthenticatedCta | null
  anonymousSignupCta: (() => void) | null
  formatLabel: (label: string) => string
  isBillingUnavailable: boolean
  isCheckoutInFlight: boolean
  isLoadingAccountState: boolean
}) {
  const className = `group/btn mt-auto inline-flex h-11 items-center justify-center gap-2 self-stretch rounded-xl px-4 text-sm font-semibold transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${
    plan.highlighted
      ? 'bg-white text-black hover:bg-white/90'
      : 'border border-white/15 text-white hover:bg-white/[0.08]'
  }`

  // Read by MarketingTracker (marketing_cta_clicked); checkout has its own events.
  const track = { 'data-cta': 'plan', 'data-cta-target': plan.planId }

  if (authenticatedCta) {
    return (
      <button
        type="button"
        {...track}
        className={className}
        disabled={authenticatedCta.disabled || authenticatedCta.loading || isCheckoutInFlight}
        onClick={authenticatedCta.onClick}
      >
        {authenticatedCta.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {formatLabel(authenticatedCta.label)}
        {!authenticatedCta.disabled && !authenticatedCta.loading && (
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/btn:translate-x-0.5" />
        )}
      </button>
    )
  }

  if (isLoadingAccountState) {
    return (
      <button type="button" className={className} disabled>
        <Loader2 className="h-4 w-4 animate-spin" />
        {formatLabel('Loading...')}
      </button>
    )
  }

  if (isBillingUnavailable) {
    return (
      <button type="button" className={className} disabled>
        {formatLabel('Billing unavailable')}
      </button>
    )
  }

  if (anonymousSignupCta) {
    return (
      <button type="button" {...track} className={className} onClick={anonymousSignupCta}>
        {formatLabel('Get started')}
        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/btn:translate-x-0.5" />
      </button>
    )
  }

  return (
    <a href={plan.href} target="_blank" rel="noopener noreferrer" {...track} className={className}>
      {formatLabel(plan.cta)}
      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/btn:translate-x-0.5" />
    </a>
  )
}
